import React, {
  useEffect,
  useState
} from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import AuthCode from "react-auth-code-input";
import { useDispatch, useSelector } from "react-redux";
import { toggleCurrentModal, verify2FACodeStart } from "store/actions";
import { showErrorNotification } from "store/notifications/actions";
import { ModalTitle } from "react-bootstrap";
import { startTwoFactorSetupAPI } from "apis/auth";

const STEP_SCAN = "scan";
const STEP_CODE = "code";

function TwoFactorAuth(props) {
  const isSetup = props.type === "setup";
  const [twoFACode, setTwoFACode] = useState("");
  const [codeInputKey, setCodeInputKey] = useState(0);
  const [step, setStep] = useState(isSetup ? STEP_SCAN : STEP_CODE);
  const [setup, setSetup] = useState({ loading: isSetup });
  const { verifyCode } = useSelector(state => state.twoFactorAuthReducer);
  const dispatch = useDispatch();

  const close = () => dispatch(toggleCurrentModal(""));

  useEffect(() => {
    if (!isSetup) return;
    let cancelled = false;
    startTwoFactorSetupAPI({ mfaToken: props.mfaToken })
      .then(({ result }) => {
        if (!cancelled) setSetup({
          loading: false,
          qrCode: result.qrCode,
          manualKey: result.manualKey,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        dispatch(showErrorNotification(error.message));
        close();
      });
    return () => { cancelled = true };
  }, [isSetup, props.mfaToken]);

  const verify = (code = twoFACode) => {
    if (verifyCode.loading || !code || code.length !== 6) return;
    dispatch(verify2FACodeStart({
      code,
      mfaToken: props.mfaToken,
      history: props.history,
      type: props.type,
    }));
  };

  const onCodeChange = (digits) => {
    const activeInputs = document.querySelectorAll(".twofa-input.active");
    const inputs = document.querySelectorAll(".twofa-input");
    if (activeInputs.length > digits.length)
      activeInputs[activeInputs.length - 1]?.classList.remove("active");
    else
      inputs[digits.length - 1]?.classList.add("active");
    setTwoFACode(digits);
    if (digits.length === 6) verify(digits);
  };

  useEffect(() => {
    if (verifyCode.error && verifyCode.error.length > 0) {
      setTwoFACode("");
      setCodeInputKey((key) => key + 1);
    }
  }, [verifyCode.error]);

  const renderScanStep = () => (
    <>
      <p className="mb-1">
        {props.t("Two factor authentication is required for all CRM accounts.")}
      </p>
      <p className="text-muted">
        {props.t("Scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, Authy...).")}
      </p>
      {setup.loading ? (
        <div className="my-5"><Spinner /></div>
      ) : (
        <>
          {setup.qrCode && (
            <img src={setup.qrCode} alt={props.t("2FA QR code")} width={200} height={200} />
          )}
          {setup.manualKey && (
            <p className="mt-3 text-muted">
              {props.t("Can't scan? Enter this key in the app:")}
              <br />
              <code className="user-select-all">{setup.manualKey}</code>
            </p>
          )}
        </>
      )}
      <Button
        color="primary"
        className="mt-3 w-md"
        disabled={setup.loading || !setup.qrCode}
        onClick={() => setStep(STEP_CODE)}
      >
        {props.t("Next")}
      </Button>
    </>
  );

  const renderCodeStep = () => (
    <>
      <p className="mb-4">
        {isSetup
          ? props.t("Enter the 6-digit code shown in your authenticator app to finish setup and sign in.")
          : props.t("Enter the 6-digit code from your authenticator app.")}
      </p>
      <AuthCode
        key={codeInputKey}
        characters={6}
        allowedCharacters="^[0-9]"
        containerClassName="twofa-container"
        inputClassName="twofa-input"
        onChange={onCodeChange}
      />
      <div className="mt-4 d-flex justify-content-center gap-2">
        {isSetup && (
          <Button
            color="light"
            className="w-md"
            disabled={verifyCode.loading}
            onClick={() => setStep(STEP_SCAN)}
          >
            {props.t("Back")}
          </Button>
        )}
        <Button
          color="primary"
          className="w-md"
          disabled={verifyCode.loading || twoFACode.length !== 6}
          onClick={() => verify()}
        >
          {verifyCode.loading ? <Spinner size="sm" /> : props.t("Verify")}
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      isOpen={props.isOpen}
      toggle={props.toggleOpen}
      centered={true}
      size="lg"
      backdrop={isSetup ? "static" : true}
      keyboard={!isSetup}
    >
      <ModalHeader toggle={close}>
        <ModalTitle>
          {isSetup ? props.t("Set up two factor authentication") : props.t("Two Factor Authentication Code")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody className="text-center my-2">
        {isSetup && (
          <p className="text-muted small mb-3">
            {props.t("Step {{current}} of {{total}}", {
              current: step === STEP_SCAN ? 1 : 2,
              total: 2,
            })}
          </p>
        )}
        {step === STEP_SCAN ? renderScanStep() : renderCodeStep()}
      </ModalBody>
    </Modal>
  );
}
export default withTranslation()(TwoFactorAuth);
